import { useState, InputHTMLAttributes, forwardRef } from 'react'
import { cn } from '@/lib/utils'
import Input from './Input'
import { useTheme } from '@/contexts/ThemeContext'

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
  helperText?: string
  themeOverride?: 'light' | 'dark'
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, label, error, helperText, themeOverride, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false)
    const { theme: contextTheme } = useTheme()
    const theme = themeOverride || contextTheme

      return (
        <div className="w-full">
          {label && (
            <label className={cn(
              "block text-sm font-medium mb-2",
              theme === 'dark' ? 'text-primary-light' : 'text-primary-lightText'
            )}>{label}</label>
          )}
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={cn(
                'flex h-10 w-full rounded-lg border pl-3 pr-10 py-2 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-gold focus-visible:border-primary-gold',
                'disabled:cursor-not-allowed disabled:opacity-50',
                theme === 'dark'
                  ? 'border-primary-blue bg-primary-dark-secondary text-primary-light placeholder:text-primary-light/50'
                  : 'border-gray-200 bg-white text-primary-lightText placeholder:text-primary-lightTextSecondary',
                error && 'border-red-500 focus-visible:ring-red-500',
                className
              )}
              ref={ref}
              {...props}
            />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-gold hover:text-primary-gold/80 active:text-primary-gold transition-colors outline-none focus:outline-none border-none focus:border-none rounded p-1.5 -mr-1.5"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onMouseDown={e => e.preventDefault()}
          >
            {showPassword ? (
              <svg
                className="w-5 h-5 stroke-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 stroke-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        </div>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        {helperText && !error && (
          <p className={cn(
            "mt-1 text-sm",
            theme === 'dark' ? 'text-primary-light/70' : 'text-primary-lightTextSecondary'
          )}>{helperText}</p>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'

export default PasswordInput
